import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  pn,
  ScreenType,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  TestLevel,
  TestType,
  TestTypeEditResource,
  TestTypeViewResource,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { BasicInfo } from "./BasicInfo";
import { TestTypePageHeader } from "./TestTypePageHeader";
import { TestLevels } from "./TestLevels/TestLevels";
import produce from "immer";

const routeName = "testType";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

export const testTypePageRoute: PageRoute<Params> = {
  handle: "Test Type",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const TestTypePage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [testType, setTestType] = useState<TestType>({} as TestType);
  const [initTestType, setInitTestType] = useState<TestType>(testType);
  const [viewResource, setViewResource] = useState<TestTypeViewResource>({});
  const [editResource, setEditResource] = useState<TestTypeEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [testType] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.testTypes.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.testTypes.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${testTypePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

  const fetch = (id: number) => {
    ApiService.testTypes.get(id).then((response) => {
      setTestType(response);
      setInitTestType(response);
      resetUnsavedChanges([response]);
    });
    };

    const pageHeader = (
        <TestTypePageHeader
            rootPermission={ROOT_PERMISSION}
            handle={testTypePageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            testType={testType}
            setTestType={setTestType}
            initTestType={initTestType}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
        />
    );

    const pageBody = (
        <Stack spacing="lg">
            <BasicInfo
                crudMode={params.crudMode}
                testType={testType}
                setTestType={setTestType}
                viewResource={viewResource}
                editResource={editResource}
                validationErrors={validationErrors}
            />

            <TestLevels
                testType={testType}
                rootPermission={ROOT_PERMISSION}
                crudMode={params.crudMode}
                testLevels={testType.manualTestLevels || []}
                setTestLevels={(value) =>
                    setTestType(
                        produce(testType, (draft) => {
                            draft.manualTestLevels = value;
                        })
                    )
                }
                validationErrors={validationErrors[pn<TestType>("manualTestLevels")] || []}
                generalValidationErrors={
                    validationErrors[pn<TestType>("manualTestLevelGeneralValidationErrors")] || []
                }
                onEdit={() => reroute(testType.id, "edit")}
            />
        </Stack>
    );

  return (
      <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="md"
          header={pageHeader}
          content={pageBody}
      />
  );
};
